import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLeague } from '@/contexts/LeagueContext';

export interface LeagueMessage {
  id: string;
  league_id: string;
  user_id: string;
  sender_name: string;
  content: string;
  is_system: boolean;
  is_admin?: boolean;
  created_at: string;
}

export interface DirectMessageRow {
  id: string;
  league_id: string;
  sender_id: string;
  recipient_id: string;
  sender_name: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

export interface DMConversation {
  recipientId: string;
  recipientName: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
}

export function useLeagueChat() {
  const { user } = useAuth();
  const { activeLeague } = useLeague();
  const leagueId = activeLeague?.id;

  const [leagueMessages, setLeagueMessages] = useState<LeagueMessage[]>([]);
  const [dmConversations, setDmConversations] = useState<DMConversation[]>([]);
  const [activeDmMessages, setActiveDmMessages] = useState<DirectMessageRow[]>([]);
  const [leagueMembers, setLeagueMembers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.id;
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Anonymous';

  // Fetch league members
  useEffect(() => {
    if (!leagueId) return;
    const fetchMembers = async () => {
      const { data } = await supabase
        .from('league_members')
        .select('user_id')
        .eq('league_id', leagueId);
      if (!data) return;

      const userIds = data.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIds);

      if (profiles) {
        setLeagueMembers(profiles.map(p => ({
          id: p.id,
          name: p.display_name || 'Unknown',
        })));
      }
    };
    fetchMembers();
  }, [leagueId]);

  // Fetch league messages
  useEffect(() => {
    if (!leagueId) { setLoading(false); return; }
    const fetchMessages = async () => {
      const { data } = await supabase
        .from('league_messages')
        .select('*')
        .eq('league_id', leagueId)
        .order('created_at', { ascending: true })
        .limit(100);
      if (data) setLeagueMessages(data as LeagueMessage[]);
      setLoading(false);
    };
    fetchMessages();

    // Realtime subscription
    const channel = supabase
      .channel(`league-chat-${leagueId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'league_messages',
        filter: `league_id=eq.${leagueId}`,
      }, (payload) => {
        setLeagueMessages(prev => [...prev, payload.new as LeagueMessage]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [leagueId]);

  // Fetch DM conversations
  const fetchConversations = useCallback(async () => {
    if (!userId || !leagueId) return;
    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .eq('league_id', leagueId)
      .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(200);

    if (!data) return;
    const rows = data as DirectMessageRow[];

    // Group by conversation partner
    const convMap = new Map<string, DMConversation>();
    for (const msg of rows) {
      const partnerId = msg.sender_id === userId ? msg.recipient_id : msg.sender_id;
      if (!convMap.has(partnerId)) {
        const partnerName = msg.sender_id === userId
          ? leagueMembers.find(m => m.id === partnerId)?.name || 'Unknown'
          : msg.sender_name;
        convMap.set(partnerId, {
          recipientId: partnerId,
          recipientName: partnerName,
          lastMessage: msg.content,
          lastMessageTime: msg.created_at,
          unreadCount: 0,
        });
      }
      if (msg.recipient_id === userId && !msg.read_at) {
        const conv = convMap.get(partnerId)!;
        conv.unreadCount++;
      }
    }
    setDmConversations(Array.from(convMap.values()));
  }, [userId, leagueId, leagueMembers]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // DM realtime
  useEffect(() => {
    if (!userId || !leagueId) return;
    const channel = supabase
      .channel(`dms-${leagueId}-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `league_id=eq.${leagueId}`,
      }, (payload) => {
        const msg = payload.new as DirectMessageRow;
        if (msg.sender_id === userId || msg.recipient_id === userId) {
          setActiveDmMessages(prev => [...prev, msg]);
          fetchConversations();
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, leagueId]);

  // Load DM thread
  const loadDmThread = useCallback(async (partnerId: string) => {
    if (!userId || !leagueId) return;
    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .eq('league_id', leagueId)
      .or(`and(sender_id.eq.${userId},recipient_id.eq.${partnerId}),and(sender_id.eq.${partnerId},recipient_id.eq.${userId})`)
      .order('created_at', { ascending: true })
      .limit(100);
    if (data) setActiveDmMessages(data as DirectMessageRow[]);

    // Mark as read
    await supabase
      .from('direct_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('league_id', leagueId)
      .eq('sender_id', partnerId)
      .eq('recipient_id', userId)
      .is('read_at', null);

    fetchConversations();
  }, [userId, leagueId, fetchConversations]);

  // Send league message
  const sendLeagueMessage = useCallback(async (content: string) => {
    if (!leagueId || !userId) return;
    await supabase.from('league_messages').insert({
      league_id: leagueId,
      user_id: userId,
      sender_name: displayName,
      content,
    });
    // Immediately refetch so the sender sees their own message
    const { data } = await supabase
      .from('league_messages')
      .select('*')
      .eq('league_id', leagueId)
      .order('created_at', { ascending: true })
      .limit(100);
    if (data) setLeagueMessages(data as LeagueMessage[]);
  }, [leagueId, userId, displayName]);

  // Send DM
  const sendDirectMessage = useCallback(async (recipientId: string, content: string) => {
    if (!leagueId || !userId) return;
    await supabase.from('direct_messages').insert({
      league_id: leagueId,
      sender_id: userId,
      recipient_id: recipientId,
      sender_name: displayName,
      content,
    });
  }, [leagueId, userId, displayName]);

  const totalUnread = dmConversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return {
    leagueMessages,
    dmConversations,
    activeDmMessages,
    leagueMembers: leagueMembers.filter(m => m.id !== userId),
    loading,
    totalUnread,
    userId,
    sendLeagueMessage,
    sendDirectMessage,
    loadDmThread,
    fetchConversations,
    hasLeague: !!leagueId,
  };
}
